import Ajv from "ajv";
import { FormModalData } from "@/components/common/FormModal";

// Initialize AJV with basic configuration
const ajv = new Ajv({ allErrors: true, useDefaults: true });

/**
 * Converts a JSON Schema to FormModalData array for dynamic form generation
 *
 * This function takes a JSON Schema object and converts it to the FormModalData format
 * that the FormModal component expects. It handles:
 * - Field types (string, number, etc.)
 * - Validation rules (pattern, minLength, maxLength, etc.)
 * - Required fields
 * - Format validation (email, url, tel)
 * - Field labels and descriptions
 *
 * @param schema - The JSON Schema object to convert
 * @returns Array of FormModalData objects ready for FormModal
 *
 * @example
 * ```typescript
 * const schema = {
 *   type: "object",
 *   properties: {
 *     email: {
 *       type: "string",
 *       format: "email",
 *       description: "User email address"
 *     },
 *     apiKey: {
 *       type: "string",
 *       minLength: 1,
 *       description: "API key for authentication"
 *     }
 *   },
 *   required: ["email", "apiKey"]
 * };
 *
 * const formFields = convertSchemaToFormModalData(schema);
 * ```
 */
type ConvertOptions = {
  isSuperAdmin?: boolean;
  readOnlyHint?: string;
};

export function convertSchemaToFormModalData(
  schema: any,
  t: any,
  options?: ConvertOptions
): FormModalData[] {
  const fields: FormModalData[] = [];

  if (
    !schema ||
    typeof schema !== "object" ||
    typeof schema.properties !== "object"
  ) {
    return fields;
  }

  // Handle JSON Schema format
  let properties = schema.properties;
  let required = schema.required || [];

  Object.entries(properties).forEach(([key, value]: [string, any]) => {
    // Skip the "type" field if it's a const field (like "no_auth", "client_credentials", etc.)
    if (value.const) {
      return;
    }

    const field: FormModalData = {
      labels: {
        // `titleKey` is namespace-prefixed, since `t` is the validation namespace.
        label:
          (typeof value.titleKey === "string" ? t(value.titleKey) : undefined) ||
          value.title ||
          value.description ||
          key,
        isRequired: required.includes(key) ? t("required") : "",
        notValid: getValidationMessage(value, t),
      },
      type: "text",
      value: value.default || "",
      regex: undefined,
    };

    // Store the original field name for better form submission handling
    (field as any).fieldName = key;

    // Determine field type based on schema
    if (value.type === "string") {
      if (value.format === "email") {
        field.type = "email";
      } else if (value.format === "uri" || value.format === "url") {
        field.type = "url";
      } else if (value.format === "tel") {
        field.type = "tel";
      } else {
        // Check if this looks like a password field
        const fieldName = key.toLowerCase();
        if (
          fieldName.includes("password") ||
          fieldName.includes("secret") ||
          fieldName.includes("token")
        ) {
          field.type = "password";
        } else {
          field.type = "text";
        }
      }
    } else if (value.type === "number" || value.type === "integer") {
      field.type = "number";
    }

    // Add regex validation if pattern is provided
    if (value.pattern) {
      try {
        field.regex = new RegExp(value.pattern);
      } catch (error) {
        console.warn(`Invalid regex pattern for field ${key}:`, value.pattern);
      }
    }

    // Handle number field validation with min/max values
    if (
      (value.type === "number" || value.type === "integer") &&
      (value.minimum !== undefined || value.maximum !== undefined)
    ) {
      // Store min/max values directly in the field for validation
      field.minimum = value.minimum;
      field.maximum = value.maximum;
    }

    // Handle format-based validation
    if (value.format && !value.pattern) {
      switch (value.format) {
        case "email":
          field.regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          break;
        case "uri":
        case "url":
          field.regex = /^https?:\/\/.+/;
          break;
        case "tel":
          field.regex = /^[\+]?[1-9][\d]{0,15}$/;
          break;
      }
    }

    // Add minLength validation for strings
    if (value.minLength && value.type === "string") {
      const minLengthRegex = new RegExp(`.{${value.minLength},}`);
      if (field.regex) {
        // Combine existing regex with minLength
        try {
          field.regex = new RegExp(
            `^(?=.*${field.regex.source})${minLengthRegex.source}$`
          );
        } catch (error) {
          console.warn(`Could not combine regex patterns for field ${key}`);
          field.regex = minLengthRegex;
        }
      } else {
        field.regex = minLengthRegex;
      }
    }

    // Apply role-based readOnly if marked super-admin only
    const isFieldSuperAdminOnly = value?.["x-internal-only"] === true;

    if (isFieldSuperAdminOnly && options?.isSuperAdmin === false) {
      field.readOnly = true;
      if (options?.readOnlyHint) {
        field.readOnlyHint = options.readOnlyHint;
      }
    }

    fields.push(field);
  });

  return fields;
}

/**
 * Generates appropriate validation messages based on schema field properties
 */
function getValidationMessage(schemaField: any, t: any): string {
  if (schemaField.format === "email") {
    return t("validEmail");
  }
  if (schemaField.format === "uri" || schemaField.format === "url") {
    return t("validUrl");
  }
  if (schemaField.format === "tel") {
    return t("validPhone");
  }
  if (schemaField.pattern) {
    return t("validValue");
  }
  if (schemaField.minLength) {
    return t("minLength", { length: schemaField.minLength });
  }
  if (schemaField.maxLength) {
    return t("maxLength", { length: schemaField.maxLength });
  }
  if (schemaField.minimum !== undefined) {
    return t("minValue", { value: schemaField.minimum });
  }
  if (schemaField.maximum !== undefined) {
    return t("maxValue", { value: schemaField.maximum });
  }
  return t("validValue");
}

/**
 * Validates form data against a JSON Schema using AJV
 *
 * @param formData - The form data to validate
 * @param schema - The JSON Schema to validate against
 * @returns Object with validation result and any errors
 */
export function validateFormData(
  formData: Record<string, any>,
  schema: any
): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  try {
    const validate = ajv.compile(schema);
    const isValid = validate(formData);

    if (!isValid && validate.errors) {
      validate.errors.forEach((error) => {
        const fieldName =
          error.instancePath.replace("/", "") || error.params.missingProperty;
        if (fieldName) {
          errors[fieldName] = error.message || "Invalid value";
        }
      });
    }

    return { isValid: !!isValid, errors };
  } catch (error) {
    console.error("Schema validation error:", error);
    return { isValid: false, errors: { general: "Schema validation failed" } };
  }
}

/**
 * Validates a single FormModal field value
 *
 * @param field - The FormModal field to validate
 * @param value - The value to validate
 * @returns Error message if invalid, null if valid
 */
export function validateFormModalField(
  field: FormModalData,
  value: any
): string | null {
  const { labels, regex } = field;

  // Check required
  if (labels.isRequired && (!value || value.toString().trim() === "")) {
    return labels.isRequired;
  }

  // Skip validation if value is empty and not required
  if (!value || value.toString().trim() === "") {
    return null;
  }

  // Check regex pattern
  if (regex && !regex.test(value)) {
    return labels.notValid;
  }

  return null;
}
